import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '@/pages/dashboard/components/DashboardLayout';
import { useToast } from '@/components/base/Toast';

// Función para leer query params
function useQueryParams() {
  const params = new URLSearchParams(window.location.search);
  return {
    obraId: params.get('obraId') || '',
    clienteId: params.get('clienteId') || '',
    obraNombre: params.get('obraNombre') || '',
    clienteNombre: params.get('clienteNombre') || '',
    cotizacionId: params.get('cotizacionId') || '',
    modo: params.get('modo') || 'crear', // 'crear', 'ver', 'editar'
  };
}

// ---------- Tipos ----------
interface Concepto {
  id: string;
  nombre: string;
  descripcion: string;
  unidad: string;
  precio_publico: number | null;
  precio_proveedor: number | null;
  esVariable: boolean;
  moneda: 'MXN' | 'USD';
}

interface Partida {
  id: string;
  conceptoNombre: string;
  unidad: string;
  cantidad: number;
  precioUnitario: number;
  subtotal: number;
  esVariable: boolean;
  moneda: 'MXN' | 'USD';
  precioProveedor: number | null;
}

// ---------- Helpers ----------
const API_URL =  'https://api.airmex.mx/api/v1';

function getToken() {
  return localStorage.getItem('airmex_token') || sessionStorage.getItem('airmex_token');
}

async function fetchWithAuth(url: string, options: RequestInit = {}) {
  const token = getToken();
  const headers: Record<string, string> = {
    ...(options.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
    ...(token && { Authorization: `Bearer ${token}` }),
    ...((options.headers as Record<string, string>) || {}),
  };
  const res = await fetch(`${API_URL}${url}`, { ...options, headers });
  if (!res.ok) {
    let mensaje = 'Error en la petición';
    try {
      const errorData = await res.json();
      if (errorData.detail) {
        if (Array.isArray(errorData.detail)) {
          mensaje = errorData.detail
            .map((e: any) => {
              const campo = e.loc ? e.loc.join('.') : '';
              return campo ? `${campo}: ${e.msg}` : e.msg;
            })
            .join(' | ');
        } else if (typeof errorData.detail === 'object') {
          mensaje = JSON.stringify(errorData.detail);
        } else {
          mensaje = errorData.detail;
        }
      } else if (errorData.message) {
        mensaje = errorData.message;
      }
    } catch {
      // No es JSON, dejamos mensaje genérico
    }
    throw new Error(mensaje);
  }
  if (res.status === 204) return null;
  return res.json();
}

function formatMXN(monto: number): string {
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 0 }).format(monto);
}

function formatUSD(monto: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(monto);
}

export default function NuevaCotizacionPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();

  // Obtener parámetros de URL
  const { obraId, clienteId, obraNombre, clienteNombre, cotizacionId, modo } = useQueryParams();
  const esSoloLectura = modo === 'ver';
  const esEdicion = modo === 'editar';

  // Datos de conceptos
  const [conceptos, setConceptos] = useState<Concepto[]>([]);
  const [numeroCot, setNumeroCot] = useState('');
  const [ivaPorcentaje, setIvaPorcentaje] = useState<number>(16);

  // Búsqueda de concepto
  const [conceptoSearch, setConceptoSearch] = useState('');
  const [selectedConcepto, setSelectedConcepto] = useState<Concepto | null>(null);
  const [cantidad, setCantidad] = useState('1');
  const [precioUnitario, setPrecioUnitario] = useState('');
  const [precioProveedor, setPrecioProveedor] = useState('');
  const [moneda, setMoneda] = useState<'MXN' | 'USD'>('MXN');

  // Partidas
  const [partidas, setPartidas] = useState<Partida[]>([]);

  // Cargar conceptos al montar
  useEffect(() => {
    fetchWithAuth('/conceptos/')
      .then(setConceptos)
      .catch(err => showToast('error', 'Error al cargar conceptos', err.message));
  }, []);

  // Autogenerar número de cotización solo si no hay cotizacionId
  useEffect(() => {
    if (!cotizacionId) {
      const now = new Date();
      const y = now.getFullYear();
      const m = String(now.getMonth() + 1).padStart(2, '0');
      const d = String(now.getDate()).padStart(2, '0');
      const rand = Math.floor(Math.random() * 9000) + 1000;
      setNumeroCot(`COT-${y}${m}${d}-${rand}`);
    }
  }, [cotizacionId]);

  // Cargar cotización existente si hay ID
  useEffect(() => {
    if (cotizacionId) {
      fetchWithAuth(`/cotizaciones/${cotizacionId}`)
        .then((data) => {
          setNumeroCot(data.numero);
          setIvaPorcentaje(data.ivaPorcentaje ?? 16);
          setPartidas(data.partidas.map((p: any) => ({
            id: p.id,
            conceptoNombre: p.conceptoNombre,
            unidad: p.unidad,
            cantidad: p.cantidad,
            precioUnitario: p.precioUnitario,
            subtotal: p.subtotal,
            esVariable: p.esVariable,
            moneda: p.moneda,
            precioProveedor: p.precioProveedor,
          })));
        })
        .catch(err => showToast('error', 'Error al cargar cotización', err.message));
    }
  }, [cotizacionId]);

  // Filtrado de conceptos para autocompletado
  const conceptosFiltrados = useMemo(() => {
    if (!conceptoSearch.trim()) return [];
    const s = conceptoSearch.toLowerCase();
    return conceptos.filter(c =>
      c.nombre.toLowerCase().includes(s) ||
      c.descripcion.toLowerCase().includes(s)
    ).slice(0, 20);
  }, [conceptos, conceptoSearch]);

  // Al seleccionar concepto, rellenar precio público y proveedor si existen
  const handleSelectConcepto = (c: Concepto) => {
    setSelectedConcepto(c);
    setConceptoSearch(c.nombre);
    setMoneda(c.moneda || 'MXN');
    setPrecioUnitario(c.precio_publico != null ? c.precio_publico.toString() : '');
    setPrecioProveedor(c.precio_proveedor != null ? c.precio_proveedor.toString() : '');
  };

  // Agregar partida (solo si no es solo lectura)
  const handleAgregarPartida = () => {
    if (esSoloLectura) return;
    if (!selectedConcepto || !cantidad || !precioUnitario) return;
    const cant = parseFloat(cantidad);
    const pu = parseFloat(precioUnitario);
    const pp = precioProveedor ? parseFloat(precioProveedor) : null;
    if (isNaN(cant) || cant <= 0 || isNaN(pu) || pu < 0) return;

    const nuevaPartida: Partida = {
      id: `part-${Date.now()}-${Math.random()}`,
      conceptoNombre: selectedConcepto.nombre,
      unidad: selectedConcepto.unidad,
      cantidad: cant,
      precioUnitario: pu,
      subtotal: cant * pu,
      esVariable: selectedConcepto.esVariable,
      moneda,
      precioProveedor: pp,
    };
    setPartidas(prev => [...prev, nuevaPartida]);
    setSelectedConcepto(null);
    setConceptoSearch('');
    setCantidad('1');
    setPrecioUnitario('');
    setPrecioProveedor('');
  };

  const handleEliminarPartida = (id: string) => {
    if (esSoloLectura) return;
    setPartidas(prev => prev.filter(p => p.id !== id));
  };

  // Cálculos de totales
  const totales = useMemo(() => {
    let totalMXN = 0;
    let totalUSD = 0;
    let gananciaMXN = 0;
    let gananciaUSD = 0;

    partidas.forEach(p => {
      const subtotal = p.subtotal;
      const gananciaPartida = (p.precioProveedor != null)
        ? (p.precioUnitario - p.precioProveedor) * p.cantidad
        : 0;
      if (p.moneda === 'USD') {
        totalUSD += subtotal;
        gananciaUSD += gananciaPartida;
      } else {
        totalMXN += subtotal;
        gananciaMXN += gananciaPartida;
      }
    });

    const ivaMXN = totalMXN * ivaPorcentaje / 100;
    const ivaUSD = totalUSD * ivaPorcentaje / 100;

    return {
      totalMXN,
      totalUSD,
      gananciaMXN,
      gananciaUSD,
      ivaMXN,
      ivaUSD,
      totalMXNConIVA: totalMXN + ivaMXN,
      totalUSDConIVA: totalUSD + ivaUSD,
    };
  }, [partidas, ivaPorcentaje]);

  // Guardar cotización (crear o editar)
  const handleGuardar = async () => {
    if (esSoloLectura) return;
    if (!obraId) {
      showToast('warning', 'Obra no seleccionada', 'No se ha especificado la obra para la cotización.');
      return;
    }
    if (partidas.length === 0) {
      showToast('warning', 'Sin partidas', 'Agrega al menos una partida a la cotización.');
      return;
    }

    const payload = {
  numero: numeroCot,
  fecha: new Date().toISOString().split('T')[0],
  monto: totales.totalMXNConIVA + totales.totalUSDConIVA * 18,   // ← AÑADIR ESTA LÍNEA
  ivaPorcentaje,
  totalMXN: totales.totalMXN,
  totalUSD: totales.totalUSD,
  ivaMXN: totales.ivaMXN,
  ivaUSD: totales.ivaUSD,
  totalMXNConIVA: totales.totalMXNConIVA,
  totalUSDConIVA: totales.totalUSDConIVA,
  gananciaMXN: totales.gananciaMXN,
  gananciaUSD: totales.gananciaUSD,
  partidas: partidas.map(p => ({
    id: p.id,
    conceptoNombre: p.conceptoNombre,
    unidad: p.unidad,
    cantidad: p.cantidad,
    precioUnitario: p.precioUnitario,
    subtotal: p.subtotal,
    esVariable: p.esVariable,
    moneda: p.moneda,
    precioProveedor: p.precioProveedor,
  })),
};

  

    try {
      if (cotizacionId && esEdicion) {
        await fetchWithAuth(`/cotizaciones/editar/${cotizacionId}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
        showToast('success', 'Cotización actualizada', `N° ${numeroCot} modificada correctamente.`);
      } else {
        await fetchWithAuth(`/obras/${obraId}/cotizaciones`, {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        showToast('success', 'Cotización creada', `N° ${numeroCot} guardada correctamente.`);
      }
      navigate(-1);
    } catch (err: any) {
      console.error('Error al guardar cotización:', err);
      showToast('error', 'Error al guardar', err.message);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-[1200px] mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl font-semibold text-foreground-900">
              {esSoloLectura ? 'Ver Cotización' : esEdicion ? 'Editar Cotización' : 'Nueva Cotización'}
            </h1>
            <p className="text-sm text-foreground-500 mt-1">
              {esSoloLectura ? 'Detalle de la cotización.' : esEdicion ? 'Modifica los conceptos y montos de la cotización.' : 'Crea una cotización dinámica seleccionando conceptos y calculando ganancia.'}
            </p>
          </div>
          <button onClick={() => navigate(-1)} className="px-4 py-2 rounded-lg border border-background-200 text-sm font-medium text-foreground-600 hover:bg-background-100">
            <i className="ri-arrow-left-line mr-1" /> Volver
          </button>
        </div>

        {/* Información de la obra y del IVA */}
        <div className="bg-white rounded-xl border border-background-200 p-5">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Cliente</label>
              <input
                type="text"
                value={clienteNombre}
                readOnly
                className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-700"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Obra</label>
              <input
                type="text"
                value={obraNombre}
                readOnly
                className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-700"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">N° Cotización</label>
              <input
                type="text"
                value={numeroCot}
                readOnly
                className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-700 font-mono"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">IVA (%)</label>
              <input
                type="number"
                value={ivaPorcentaje}
                onChange={(e) => setIvaPorcentaje(parseFloat(e.target.value) || 0)}
                disabled={esSoloLectura}
                min="0"
                step="0.01"
                className="w-full px-3 py-2 rounded-lg border border-background-200 bg-white text-sm text-foreground-700 disabled:bg-background-50"
              />
            </div>
          </div>
        </div>

        {/* Buscador de conceptos (solo creación/edición) */}
        {!esSoloLectura && (
          <div className="bg-white rounded-xl border border-background-200 p-5">
            <h3 className="font-heading font-semibold text-sm text-foreground-800 mb-3">Agregar concepto</h3>
            <div className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
              <div className="md:col-span-3 relative">
                <label className="block text-xs font-medium text-foreground-600 mb-1.5">Buscar concepto</label>
                <input
                  type="text"
                  value={conceptoSearch}
                  onChange={(e) => { setConceptoSearch(e.target.value); setSelectedConcepto(null); }}
                  placeholder="Escribe para buscar..."
                  className="w-full px-3 py-2 rounded-lg border border-background-200 text-sm"
                />
                {conceptoSearch && !selectedConcepto && conceptosFiltrados.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-background-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {conceptosFiltrados.map(c => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => handleSelectConcepto(c)}
                        className="w-full text-left px-3 py-2 hover:bg-background-50 text-sm flex justify-between items-center"
                      >
                        <span>{c.nombre} <span className="text-xs text-foreground-400">({c.unidad})</span></span>
                        <span className="text-xs text-foreground-500">{c.moneda}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1.5">Cantidad</label>
                <input type="number" value={cantidad} onChange={(e) => setCantidad(e.target.value)} min="0.01" step="any" className="w-full px-3 py-2 rounded-lg border border-background-200 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1.5">Precio Público</label>
                <input type="number" value={precioUnitario} onChange={(e) => setPrecioUnitario(e.target.value)} min="0" step="any" className="w-full px-3 py-2 rounded-lg border border-background-200 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1.5">Precio Proveedor</label>
                <input type="number" value={precioProveedor} onChange={(e) => setPrecioProveedor(e.target.value)} min="0" step="any" className="w-full px-3 py-2 rounded-lg border border-background-200 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1.5">Moneda</label>
                <select value={moneda} onChange={(e) => setMoneda(e.target.value as 'MXN' | 'USD')} className="w-full px-3 py-2 rounded-lg border border-background-200 text-sm">
                  <option value="MXN">MXN</option>
                  <option value="USD">USD</option>
                </select>
              </div>
              <div className="flex items-end">
                <button
                  onClick={handleAgregarPartida}
                  disabled={!selectedConcepto || !cantidad || !precioUnitario}
                  className="w-full px-4 py-2 rounded-lg bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 disabled:bg-background-200 disabled:text-foreground-400"
                >
                  <i className="ri-add-line mr-1" /> Agregar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tabla de partidas */}
        <div className="bg-white rounded-xl border border-background-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-background-100/50">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500">Concepto</th>
                <th className="text-center px-3 py-3 text-xs font-semibold text-foreground-500">Unidad</th>
                <th className="text-center px-3 py-3 text-xs font-semibold text-foreground-500">Cant.</th>
                <th className="text-right px-3 py-3 text-xs font-semibold text-foreground-500">P. Público</th>
                <th className="text-right px-3 py-3 text-xs font-semibold text-foreground-500">P. Proveedor</th>
                <th className="text-right px-3 py-3 text-xs font-semibold text-foreground-500">Subtotal</th>
                <th className="text-right px-3 py-3 text-xs font-semibold text-foreground-500">Ganancia</th>
                {!esSoloLectura && <th className="w-8"></th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-background-100">
              {partidas.map(p => (
                <tr key={p.id} className="hover:bg-background-50/50">
                  <td className="px-4 py-3 text-xs font-medium">{p.conceptoNombre}</td>
                  <td className="px-3 py-3 text-center text-xs">{p.unidad}</td>
                  <td className="px-3 py-3 text-center text-xs">{p.cantidad}</td>
                  <td className="px-3 py-3 text-right text-xs">{p.moneda === 'USD' ? formatUSD(p.precioUnitario) : formatMXN(p.precioUnitario)}</td>
                  <td className="px-3 py-3 text-right text-xs">{p.precioProveedor != null ? (p.moneda === 'USD' ? formatUSD(p.precioProveedor) : formatMXN(p.precioProveedor)) : '—'}</td>
                  <td className="px-3 py-3 text-right text-xs font-semibold">{p.moneda === 'USD' ? formatUSD(p.subtotal) : formatMXN(p.subtotal)}</td>
                  <td className="px-3 py-3 text-right text-xs font-semibold text-green-600">
                    {p.precioProveedor != null ? (p.moneda === 'USD' ? formatUSD((p.precioUnitario - p.precioProveedor) * p.cantidad) : formatMXN((p.precioUnitario - p.precioProveedor) * p.cantidad)) : '—'}
                  </td>
                  {!esSoloLectura && (
                    <td className="px-2 py-3 text-center">
                      <button onClick={() => handleEliminarPartida(p.id)} className="w-6 h-6 rounded hover:bg-red-100 text-foreground-400 hover:text-red-500">
                        <i className="ri-close-line text-sm" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
              {partidas.length === 0 && (
                <tr>
                  <td colSpan={esSoloLectura ? 7 : 8} className="py-10 text-center text-xs text-foreground-400">
                    No hay partidas. {!esSoloLectura && 'Agrega conceptos para comenzar.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Totales */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-background-200 p-4">
            <p className="text-xs text-foreground-400">Total MXN (sin IVA)</p>
            <p className="text-xl font-bold text-foreground-900">{formatMXN(totales.totalMXN)}</p>
            <p className="text-xs text-foreground-400 mt-1">IVA ({ivaPorcentaje}%)</p>
            <p className="text-sm font-semibold text-foreground-700">{formatMXN(totales.ivaMXN)}</p>
            <p className="text-xs text-foreground-400 mt-1">Total MXN con IVA</p>
            <p className="text-lg font-bold text-foreground-900">{formatMXN(totales.totalMXNConIVA)}</p>
          </div>
          <div className="bg-white rounded-xl border border-background-200 p-4">
            <p className="text-xs text-foreground-400">Total USD (sin IVA)</p>
            <p className="text-xl font-bold text-foreground-900">{formatUSD(totales.totalUSD)}</p>
            <p className="text-xs text-foreground-400 mt-1">IVA ({ivaPorcentaje}%)</p>
            <p className="text-sm font-semibold text-foreground-700">{formatUSD(totales.ivaUSD)}</p>
            <p className="text-xs text-foreground-400 mt-1">Total USD con IVA</p>
            <p className="text-lg font-bold text-foreground-900">{formatUSD(totales.totalUSDConIVA)}</p>
          </div>
          <div className="bg-white rounded-xl border border-background-200 p-4">
            <p className="text-xs text-foreground-400">Ganancia MXN</p>
            <p className="text-xl font-bold text-green-600">{formatMXN(totales.gananciaMXN)}</p>
          </div>
          <div className="bg-white rounded-xl border border-background-200 p-4">
            <p className="text-xs text-foreground-400">Ganancia USD</p>
            <p className="text-xl font-bold text-green-600">{formatUSD(totales.gananciaUSD)}</p>
          </div>
        </div>

        {/* Botón guardar (solo creación/edición) */}
        {!esSoloLectura && (
          <div className="flex justify-end">
            <button
              onClick={handleGuardar}
              className="px-6 py-2.5 rounded-lg bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 shadow-sm shadow-primary-500/20"
            >
              <i className="ri-save-line mr-1.5" /> {esEdicion ? 'Actualizar Cotización' : 'Guardar Cotización'}
            </button>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}